/**
 * API fetch wrapper. Every request carries the Cognito access token
 * (`x-cognito-access-token`), which the backend validates via
 * cognito-idp:GetUser. Slice AUTH (2026-05-29) replaced the old
 * `x-dev-company-id` header bypass with real Cognito auth.
 */
import { withAuthHeader } from "./token";

const API_URL = (import.meta.env.VITE_API_URL ?? "").replace(/\/$/, "");

async function signedFetch(path: string, init?: RequestInit): Promise<Response> {
  if (!API_URL) throw new Error("VITE_API_URL not configured");
  const url = `${API_URL}${path}`;
  const headers = await withAuthHeader(new Headers(init?.headers));
  if (init?.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  return fetch(url, { ...init, headers });
}

async function jsonRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const resp = await signedFetch(path, init);
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`API ${path} → ${resp.status}: ${text}`);
  }
  return (await resp.json()) as T;
}

export const api = {
  get: <T>(path: string) => jsonRequest<T>(path, { method: "GET" }),
  post: <T>(path: string, body?: unknown) =>
    jsonRequest<T>(path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),
  patch: <T>(path: string, body?: unknown) =>
    jsonRequest<T>(path, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),
  delete: <T>(path: string) => jsonRequest<T>(path, { method: "DELETE" }),
  getBlob: async (path: string): Promise<Blob> => {
    const resp = await signedFetch(path, { method: "GET" });
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`API ${path} → ${resp.status}: ${text}`);
    }
    return await resp.blob();
  },
  postBlob: async (path: string): Promise<Blob> => {
    const resp = await signedFetch(path, { method: "POST" });
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`API ${path} → ${resp.status}: ${text}`);
    }
    return await resp.blob();
  },
};

// Direct S3 presigned POST — uploads bypass our API entirely.
export async function uploadToS3(
  uploadUrl: string,
  uploadFields: Record<string, string>,
  file: File
): Promise<void> {
  const form = new FormData();
  for (const [k, v] of Object.entries(uploadFields)) {
    form.append(k, v);
  }
  form.append("file", file);
  const resp = await fetch(uploadUrl, { method: "POST", body: form });
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`S3 upload failed (${resp.status}): ${text}`);
  }
}
